import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { ThirdPartyCard } from '../../components/ThirdPartyCard';
import { NoData } from '../../components/Illustrations/NoData';
import { SpeedDial } from '../../components/SpeedDial';
import {
  PATH_ORGANIZATION_RELATIONS,
  PATH_ORGANIZATION_RELATIONS_EDIT,
} from '../../app/constants/paths.constant';
import { RightDrawer } from '../../components/RightDrawer';
import { FormVariant } from '../../forms';
import { ThirdPartyFormTab } from '../../forms/ThirdParty/enums/tab.enum';
import { OrganizationForm } from '../../forms/ThirdParty/Organization';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

export const OrganizationPage_Relations_Organization_Fragment = graphql(`
  fragment OrganizationPage_Relations_Organization_Fragment on Organization {
    _id
    isInternal
    mother {
      ...OrganizationCardCmp_Organization_Fragment
    }
    children {
      _id
      ...OrganizationCardCmp_Organization_Fragment
    }
    ...ThirdPartyForm_Organization_Fragment
  }
`);

interface Props {
  readonly organization: FragmentType<
    typeof OrganizationPage_Relations_Organization_Fragment
  >;
}

export const Relations = ({ organization: pOrganization }: Readonly<Props>) => {
  const organization = getFragmentData(
    OrganizationPage_Relations_Organization_Fragment,
    pOrganization,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_ORGANIZATION_RELATIONS,
      edit: PATH_ORGANIZATION_RELATIONS_EDIT,
    },
    { id: organization._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { mother, children } = organization;

  return (
    <>
      {!mother && !children.length ? (
        <NoData />
      ) : (
        <Box sx={{ pt: 4, width: '100%' }}>
          <Grid container spacing={2}>
            <Grid
              size={{
                md: 6,
                xs: 12,
              }}
            >
              <Typography
                component="h2"
                gutterBottom
                sx={{ pb: 2 }}
                variant="h5"
              >
                Organisation Mère
              </Typography>
              {!mother ? (
                <NoData />
              ) : (
                <Grid container size={12} spacing={2}>
                  <ThirdPartyCard organization={mother} />
                </Grid>
              )}
            </Grid>
            <Grid
              size={{
                md: 6,
                xs: 12,
              }}
            >
              <Typography
                component="h2"
                gutterBottom
                sx={{ pt: { xs: 4, md: 0 }, pb: 2 }}
                variant="h5"
              >
                Organisations Filles
              </Typography>
              {!children.length ? (
                <NoData />
              ) : (
                <Grid container size={12} spacing={2}>
                  {children.map((child) => (
                    <ThirdPartyCard key={child._id} organization={child} />
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
        </Box>
      )}
      <SpeedDial
        actions={[
          {
            id: 'editOrganizationRelations',
            icon: <EditIcon />,
            name: 'Modifier Relations',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Relations de l'Organisation"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <OrganizationForm
          formVariant={FormVariant.Modification}
          initialValues={organization}
          isInternal={organization.isInternal}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          organizationId={organization._id}
          stepIdsSubset={[
            ThirdPartyFormTab.ChildOrganizations,
            ThirdPartyFormTab.MotherOrganization,
          ]}
          title="Formulaire de modification des Relations de l'Organisation"
        />
      </RightDrawer>
    </>
  );
};
